import React from 'react';
import {
  Input,
  Textarea,
  Dropdown,
  Option,
  Checkbox,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Settings24Regular } from '@fluentui/react-icons';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import CompanyPicker from '../CompanyPicker';
import LinkedCompaniesEditor from './LinkedCompaniesEditor';
import EmailDedupBanner from '../EmailDedupBanner';
import { useEmailDedupLookup } from '../../hooks/useEmailDedupLookup';
import { type CustomerFormBag } from './types';
import { COUNTRY_NAME_TO_ISO, ISO_TO_COUNTRY_NAME } from './helpers';
import { useSectionStyles } from './styles';

interface GeneralSectionProps {
  form: CustomerFormBag;
  /** Hide the title icon when the section is hosted in a context that
   *  already provides framing (e.g. the profile page's card wrapper). */
  showIcon?: boolean;
}

const useLocalStyles = makeStyles({
  // Phone number takes the remaining width; the extension is a narrow
  // trailing field. Only rendered for companies/contacts.
  phoneNumberField: {
    flexGrow: 1,
  },
  extensionField: {
    flexShrink: 0,
    width: '96px',
  },
  methodsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '20px',
    rowGap: '4px',
    marginTop: '2px',
  },
  methodsHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
});

/**
 * Identity-level fields: name, email, phone (with national-format
 * autoformatting), Company picker (Contacts only), Tax profile picker
 * (Individuals + Companies). Autosaves on each field's blur.
 */
const GeneralSection: React.FC<GeneralSectionProps> = ({ form, showIcon = true }) => {
  const styles = useSectionStyles();
  const localStyles = useLocalStyles();
  const {
    customerName, setCustomerName,
    email, setEmail,
    phone, setPhone,
    phoneExtension, setPhoneExtension,
    allowedPaymentMethods, setAllowedPaymentMethods,
    poRequired, setPoRequired,
    prWithholdingRate, setPrWithholdingRate,
    title, setTitle,
    notes, setNotes,
    country, setCountry,
    setRegion,
    customerKind,
    customerId,
    linkedCompanyId, setLinkedCompanyId,
    linkedCompanyName, setLinkedCompanyName,
    linkedCompanies, setLinkedCompanies,
    taxRegionId, setTaxRegionId,
    taxRegionsData, taxRegionsLoading,
    fieldErrors, setFieldErrors,
    saveStatus, setSaveStatus,
    triggerSave,
    variant,
  } = form;

  // Raw text buffer for the withholding-rate % input while it's focused. The
  // stored value is a fraction (0.10), but formatting it back to a percent on
  // every render strips an in-progress decimal ("10." → "10"), blocking
  // fractional entry. Hold the raw text during editing; commit to the fraction
  // on blur (which then fires the auto-save effect once, not per keystroke).
  const [rateText, setRateText] = React.useState<string | null>(null);

  // Add-mode only: surface an existing customer with the same email so staff
  // don't create a dupe. Disabled in Edit (the user IS the existing customer).
  const dedup = useEmailDedupLookup(email, { enabled: variant === 'add' });

  // GH #291: email is required on Add for individuals + contacts (people).
  // Companies are exempt — they dedupe by name and frequently lack a single
  // billing email at create time. Edit mode keeps the existing "empty allowed"
  // behavior so historical records without an email can still be updated.
  const emailRequired = variant === 'add' && customerKind !== 'company';

  // Pay-link payment methods. The stored array is the explicit allowed set;
  // an empty array means the card-only default. Derive each checkbox's state
  // from it, and rebuild the array on toggle while guaranteeing at least one
  // method stays enabled (an empty pay link would lock the customer out).
  const cardChecked = allowedPaymentMethods.length === 0 || allowedPaymentMethods.includes('card');
  const achChecked = allowedPaymentMethods.includes('ach');
  const buildMethods = (card: boolean, ach: boolean): string[] => {
    if (card && ach) return ['card', 'ach'];
    if (ach && !card) return ['ach'];
    return []; // card-only (default)
  };
  const toggleCard = (checked: boolean) => {
    if (!checked && !achChecked) return; // keep at least one method
    setAllowedPaymentMethods(buildMethods(checked, achChecked));
  };
  const toggleAch = (checked: boolean) => {
    if (!checked && !cardChecked) return; // keep at least one method
    setAllowedPaymentMethods(buildMethods(cardChecked, checked));
  };

  return (
    <>
      <div className={styles.sectionTitle}>{showIcon && <Settings24Regular />}General</div>

      <div className={styles.formField}>
        <label className={styles.label}>Name</label>
        <Input
          size="large"
          value={customerName}
          onChange={(_e, data) => setCustomerName(data.value)}
          onBlur={triggerSave}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.label}>
          Email{emailRequired ? ' *' : ''}
          {fieldErrors.email && (
            <span className={styles.fieldErrorText}>{fieldErrors.email}</span>
          )}
        </label>
        <Input
          size="large"
          value={email}
          onChange={(_e, data) => {
            setEmail(data.value);
            if (fieldErrors.email) {
              setFieldErrors(({ email: _drop, ...rest }) => rest);
            }
          }}
          onBlur={() => {
            // Client-side email shape + presence check. Empty is allowed for
            // companies; required for individuals/contacts on create (GH #291).
            // Errors clear on next edit. Force saveStatus to idle so any
            // prior 'unsaved' / 'saving' badge clears in favor of the
            // inline label error.
            const trimmed = email.trim();
            if (!trimmed && emailRequired) {
              setFieldErrors((prev) => ({ ...prev, email: 'Email is required.' }));
              setSaveStatus('idle');
              return;
            }
            if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
              setFieldErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
              setSaveStatus('idle');
              return;
            }
            triggerSave();
          }}
          style={fieldErrors.email ? { outline: `1px solid ${tokens.colorPaletteRedBorder1}` } : undefined}
        />
        {variant === 'add' && dedup.match && (
          <EmailDedupBanner match={dedup.match} additional={dedup.additional} />
        )}
      </div>

      <div className={styles.formRow}>
      <div className={mergeClasses(styles.formField, localStyles.phoneNumberField)}>
        <label className={styles.label}>
          Phone
          {fieldErrors.phone && (
            <span className={styles.fieldErrorText}>{fieldErrors.phone}</span>
          )}
        </label>
        <Input
          size="large"
          value={phone}
          inputMode="numeric"
          onKeyDown={(e) => {
            // Backspacing a formatting char (paren, space, hyphen) would put
            // it right back on the next reformat — feels stuck. Hijack and
            // delete the nearest digit before the cursor instead. Only the
            // simple no-selection case; ranges go through the default path.
            if (e.key !== 'Backspace') return;
            const input = e.currentTarget as HTMLInputElement;
            const start = input.selectionStart ?? 0;
            const end = input.selectionEnd ?? 0;
            if (start !== end || start === 0) return;
            let cursor = start - 1;
            while (cursor >= 0 && !/\d/.test(phone[cursor])) cursor--;
            if (cursor < 0 || cursor === start - 1) return;
            e.preventDefault();
            const next = phone.slice(0, cursor) + phone.slice(cursor + 1);
            const hasPlus = next.trimStart().startsWith('+');
            const digits = next.replace(/\D/g, '');
            const formatted = new AsYouType(COUNTRY_NAME_TO_ISO[country])
              .input(hasPlus ? `+${digits}` : digits);
            setPhone(formatted);
            if (fieldErrors.phone) {
              setFieldErrors(({ phone: _drop, ...rest }) => rest);
            }
          }}
          onChange={(_e, data) => {
            // Strip non-digits and re-feed through AsYouType so parens/space/
            // hyphen are auto-managed. Leading "+" preserved for international.
            // Digit cap: 11 without "+" (NANP w/ country code), 15 with "+".
            const hasPlus = data.value.trimStart().startsWith('+');
            let digits = data.value.replace(/\D/g, '');
            const maxDigits = hasPlus ? 15 : 11;
            if (digits.length > maxDigits) digits = digits.slice(0, maxDigits);
            const formatted = new AsYouType(COUNTRY_NAME_TO_ISO[country])
              .input(hasPlus ? `+${digits}` : digits);
            setPhone(formatted);
            if (fieldErrors.phone) {
              setFieldErrors(({ phone: _drop, ...rest }) => rest);
            }
          }}
          onBlur={() => {
            // Empty is allowed. Otherwise parse via libphonenumber-js with
            // the current country as default region. If parsing resolves to
            // a different country, flip the dropdown to match.
            const trimmed = phone.trim();
            if (!trimmed) {
              triggerSave();
              return;
            }
            const defaultRegion = COUNTRY_NAME_TO_ISO[country];
            const parsed = parsePhoneNumberFromString(trimmed, defaultRegion);
            if (!parsed || !parsed.isValid()) {
              setFieldErrors((prev) => ({ ...prev, phone: 'Please enter a valid phone number.' }));
              setSaveStatus('idle');
              return;
            }
            const formatted = parsed.formatNational();
            if (formatted !== phone) setPhone(formatted);
            const parsedCountryName = parsed.country ? ISO_TO_COUNTRY_NAME[parsed.country] : undefined;
            if (parsedCountryName && parsedCountryName !== country) {
              setCountry(parsedCountryName);
              setRegion('');
            }
            triggerSave();
          }}
          style={fieldErrors.phone ? { outline: `1px solid ${tokens.colorPaletteRedBorder1}` } : undefined}
        />
      </div>
      {/* Phone extension — companies/contacts only. TWM-owned column, not a
          Booqable property; plain digits, no national-format autoformatting. */}
      {(customerKind === 'company' || customerKind === 'contact') && (
        <div className={mergeClasses(styles.formField, localStyles.extensionField)}>
          <label className={styles.label}>Ext.</label>
          <Input
            size="large"
            value={phoneExtension}
            inputMode="numeric"
            maxLength={10}
            onChange={(_e, data) => setPhoneExtension(data.value.replace(/[^0-9]/g, '').slice(0, 10))}
            onBlur={triggerSave}
          />
        </div>
      )}
      </div>

      {customerKind === 'contact' && (
        <div className={styles.formField}>
          <label className={styles.label}>
            {variant === 'edit' && customerId ? 'Companies' : 'Company'}
          </label>
          {variant === 'edit' && customerId ? (
            // A contact may belong to more than one company. In edit mode the
            // editor manages the full membership set live; the primary (starred)
            // is what orders / kind-detection roll up to. onChange keeps the
            // form's primary linkedCompanyId/Name in sync.
            <LinkedCompaniesEditor
              contactId={customerId}
              linkedCompanies={linkedCompanies}
              onChange={(companies) => {
                setLinkedCompanies(companies);
                const primary = companies.find((c) => c.isPrimary) || null;
                setLinkedCompanyId(primary ? primary.id : null);
                setLinkedCompanyName(primary ? primary.name : '');
              }}
              error={!linkedCompanyId && saveStatus === 'error'}
            />
          ) : (
            // Create mode: a single primary company. Additional companies can be
            // added once the contact exists (edit mode).
            <CompanyPicker
              selectedId={linkedCompanyId}
              selectedName={linkedCompanyName}
              onChange={(id, name) => {
                setLinkedCompanyId(id);
                setLinkedCompanyName(name);
                triggerSave();
              }}
              error={
                customerKind === 'contact' && !linkedCompanyId && saveStatus === 'error'
              }
            />
          )}
        </div>
      )}

      {/* Title / role — contacts only. TWM-owned column, not a Booqable field
          (e.g. "Operations Manager"). */}
      {customerKind === 'contact' && (
        <div className={styles.formField}>
          <label className={styles.label}>Title</label>
          <Input
            size="large"
            value={title}
            onChange={(_e, data) => setTitle(data.value)}
            onBlur={triggerSave}
          />
        </div>
      )}

      {/* Billing contact / email opt-in are per-MEMBERSHIP flags — a contact
          may be the billing contact / opted in for one company but not another.
          They're edited per-company in the LinkedCompaniesEditor above, not
          here. */}

      {customerKind !== 'contact' && (
        <div className={styles.formField}>
          <label className={styles.label}>Tax profile</label>
          {/* Options key on Booqable's tax-region UUID, not the local row id —
              the customer's `tax_region_id` comes from Booqable's API and we
              save it straight back, so the form operates in Booqable IDs end
              to end. The local `tax_regions` table only serves as the option
              source. */}
          <Dropdown
            size="large"
            placeholder="Select tax profile"
            value={
              taxRegionId && taxRegionsData
                ? taxRegionsData.find((tr: any) => tr.attributes?.booqable_id === taxRegionId)?.attributes?.name || 'No tax profile'
                : 'No tax profile'
            }
            selectedOptions={taxRegionId ? [taxRegionId] : []}
            onOptionSelect={(_e, data) => setTaxRegionId(data.optionValue as string)}
            disabled={taxRegionsLoading}
            onBlur={triggerSave}
          >
            <Option value="">No tax profile</Option>
            {taxRegionsData?.map((taxRegion: any) => (
              <Option key={taxRegion.id} value={taxRegion.attributes?.booqable_id || ''}>
                {taxRegion.attributes?.name || taxRegion.id}
              </Option>
            ))}
          </Dropdown>
        </div>
      )}

      {/* Pay-link payment methods are a billing setting — only customers
          (individuals) and companies are billed. Contacts are people scoped
          to a company and never carry their own pay links. */}
      {customerKind !== 'contact' && (
        <div className={styles.formField}>
          <label className={styles.label}>Payment methods on pay links</label>
          <div className={localStyles.methodsRow}>
            <Checkbox
              size="large"
              label="Card"
              checked={cardChecked}
              onChange={(_e, data) => toggleCard(!!data.checked)}
            />
            <Checkbox
              size="large"
              label="Bank transfer (ACH)"
              checked={achChecked}
              onChange={(_e, data) => toggleAch(!!data.checked)}
            />
          </div>
          <span className={localStyles.methodsHint}>
            Controls which options appear on this customer&apos;s invoice &ldquo;Pay Now&rdquo; links.
            Card only by default.
          </span>
        </div>
      )}

      {/* PO Required (#1178) — billing setting, billed entities only. When on,
          the customer's invoices can't be finalized until a PO number is
          entered on the order. */}
      {customerKind !== 'contact' && (
        <div className={styles.formField}>
          <div className={localStyles.methodsRow}>
            <Checkbox
              size="large"
              label="Require PO number"
              checked={poRequired}
              onChange={(_e, data) => setPoRequired(!!data.checked)}
            />
          </div>
          <span className={localStyles.methodsHint}>
            Blocks finalizing this customer&apos;s invoices until a PO number is entered on the order.
          </span>
        </div>
      )}

      {/* PR services-withholding (Sec. 1062.03) — billed entities only. When on,
          this customer's invoices show a 10% (editable) holdback that reduces
          the Balance Due; the customer remits it to Hacienda on TWM's behalf. */}
      {customerKind !== 'contact' && (
        <div className={styles.formField}>
          <div className={localStyles.methodsRow}>
            <Checkbox
              size="large"
              label="Requires PR services-withholding"
              checked={prWithholdingRate != null}
              onChange={(_e, data) =>
                setPrWithholdingRate(data.checked ? 0.10 : null)}
            />
          </div>
          {prWithholdingRate != null && (
            <div className={mergeClasses(styles.formField, localStyles.extensionField)}>
              <label className={styles.label} htmlFor="pr-withholding-rate">Withholding rate (%)</label>
              <Input
                id="pr-withholding-rate"
                size="large"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={rateText ?? String(+(prWithholdingRate * 100).toFixed(2))}
                onChange={(_e, data) => setRateText(data.value)}
                onBlur={() => {
                  // Commit the raw text to the stored fraction. The
                  // prWithholdingRate change flushes the auto-save effect.
                  if (rateText !== null) {
                    const pct = parseFloat(rateText);
                    setPrWithholdingRate(
                      Number.isNaN(pct) ? 0 : Math.min(100, Math.max(0, pct)) / 100);
                    setRateText(null);
                  }
                }}
                contentAfter={<span>%</span>}
              />
            </div>
          )}
          <span className={localStyles.methodsHint}>
            Adds a Puerto Rico services-withholding deduction to this customer&apos;s
            invoices (default 10%). The total is unchanged; the held-back amount
            reduces the Balance Due and is remitted to Hacienda on TWM&apos;s behalf.
          </span>
        </div>
      )}

      {/* Internal notes — free-text, all customer kinds. TWM-owned column on the
          customer entity; distinct from the order-scoped "Customer Notes" card.
          Labeled "Internal notes" to disambiguate. */}
      <div className={styles.formField}>
        <label className={styles.label}>Internal notes</label>
        <Textarea
          size="large"
          resize="vertical"
          value={notes}
          onChange={(_e, data) => setNotes(data.value)}
          onBlur={triggerSave}
        />
      </div>
    </>
  );
};

export default GeneralSection;
